import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import styles from '../styles/configuration.module.scss';
import { displayTbOrGb } from '../helpers/sizesConverter';
import { steps } from '../constants/configuration';
import { getServices } from '../constants/configuration';
import { Dropdown } from 'react-dropdown-now';
import 'react-dropdown-now/style.css';

function InfrastructureConfig({ service, changeConfigurations, intialConfigs }) {
    const { t } = useTranslation('configuration');

    const services = getServices(t);

    const initialStorageOptions = {};
    const initialComputeOptions = {};

    const [storage, setStorage] = useState(intialConfigs.storage);
    const [compute, setCompute] = useState(intialConfigs.compute);
    const [isStorageHidden, setIsStorageHidden] = useState(true);
    const [isComputeHidden, setIsComputeHidden] = useState(true);
    const [isExternal, setIsExternal] = useState(false);
    const [storagePreset, setStoragePreset] = useState(0);
    const [computePreset, setComputePreset] = useState(0);

    const calculateUsable = (type) => parseFloat(Math.round(storage[type].nodes * storage[type].size / 3 * 10) / 10).toFixed(1);

    const internalDisk = {
        nodes: service.storageConfigs.nodes.values[0].value,
        size: service.storageConfigs.size.values[0].value,
        type: service.storageConfigs.type.values[0].value,
    };

    const emptyStorage = {
        nodes: 0,
        size: 0
    };

    const externalDisk = {
        nodes: 1,
        size: service.storageConfigs.externalValues[0].value,
        type: 0
    };

    const initialOptions = {
        hdd: internalDisk,
        ssd: internalDisk,
        isExternal: false
    };

    const externalInitialOptions = {
        hdd: externalDisk,
        ssd: externalDisk,
        isExternal: true
    };

    useEffect(() => {
        changeConfigurations({
            storage: {
                ...storage,
                hdd: storage.ssd.type === 1 ? emptyStorage : storage.hdd,
                isExternal
            },
            compute
        });
        setComputePreset(computePreset === 3 ? findValueCompute() : checkIfCustomCompute())
    }, [compute]);

    useEffect(() => {
        setStorage(isExternal ? externalInitialOptions : initialOptions);
    }, [isExternal]);

    const changeCompute = (e, field) => setCompute(prevState => ({ ...prevState, [field]: Number(e.value) }));

    const checkIfCustomStorage = (storage) => {
        let futureStep = storagePreset;
        services[3].options.forEach(option => {
            if (option.min !== undefined && storage.ssd[option.title] !== option[steps[storagePreset]] || storage.hdd[option.title] !== option[steps[storagePreset]]) {
                futureStep = 3;
                return;
            }
        });

        return futureStep;
    };

    const checkIfCustomCompute = () => {
        let futureStep = computePreset;
        services[0].options.forEach(option => {
            if (option.title && option.min !== undefined && compute[option.title] !== option[steps[computePreset]]) {
                futureStep = 3;
                return;
            }
        });

        return futureStep;
    };

    const findValueStorage = (storage) => {
        let degreeIndex = '';
        const initialValue = services[3].options[0];
        steps.forEach((step, index) => {
            if (initialValue[step] === storage.ssd[initialValue.title] && initialValue[step] === storage.hdd[initialValue.title]) {
                degreeIndex = index;
                return;
            }
        });

        services[3].options.forEach(option => {
            if (storage.ssd[option.title] !== option[steps[degreeIndex]] || storage.hdd[option.title] !== option[steps[degreeIndex]]) {
                degreeIndex = 3;
                return;
            }
        });

        return degreeIndex;
    };

    const findValueCompute = () => {
        let degreeIndex = '';
        const initialValue = services[0].options[0];
        steps.forEach((step, index) => {
            if (initialValue[step] === compute[initialValue.title]) {
                degreeIndex = index;
                return;
            }
        });

        services[0].options.forEach(option => {
            if (option.title && option.min !== undefined && compute[option.title] !== option[steps[degreeIndex]]) {
                degreeIndex = 3;
                return;
            }
        });

        return degreeIndex;
    };

    useEffect(() => {
        if (computePreset !== 3) {
            services[0].options.forEach(option => {
                if (option.title) {
                    initialComputeOptions[option.title] = option[steps[computePreset]];
                }
            });
            setCompute(initialComputeOptions);
        }
    }, [computePreset]);

    const changeStorage = (name) => (data) => {
        const [field, diskType, addDiskType] = name.split('-');
        const newState = { ...storage };
        const newValue = Number(data.value);
        const isNoHdd = field === 'type' ? newValue === 1 : storage.ssd.type === 1;

        if (diskType) {
            newState[diskType] = {
                ...newState[diskType],
                [field]: newValue
            }
        }
        if (addDiskType) {
            newState[addDiskType] = {
                ...newState[addDiskType],
                [field]: newValue
            }
        }
        setStorage(newState);
        changeConfigurations({
            storage: {
                ...newState,
                hdd: isNoHdd ? emptyStorage : newState.hdd,
                isExternal,
            },
            compute
        });
        setStoragePreset(storagePreset === 3 ? findValueStorage(newState) : checkIfCustomStorage(newState));
    };

    const changeStoragePreset = (e) => {
        const newStoragePreset = Number(e.value);
        setStoragePreset(newStoragePreset);
        if (newStoragePreset !== 3) {
            services[3].options.forEach(option => {
                if (option.title) {
                    initialStorageOptions[option.title] = option[steps[newStoragePreset]];
                }
            });
            changeConfigurations({
                storage: {
                    ...storage,
                    ssd: newStoragePreset === 0 ? emptyStorage : initialStorageOptions,
                    hdd: initialStorageOptions,
                    isExternal,
                },
                compute
            });
            setStorage(prevState => ({
                ...prevState,
                ssd: initialStorageOptions,
                hdd: initialStorageOptions
            }));
        }
    };

    return (
        <>
            <div className={styles.service}>
                <h5 style={{ fontSize: '16px' }}>{t(`infrastructure.external`)}</h5>
                <br />
                <div className='flex pointer' style={{ cursor: 'pointer' }} onClick={() => setIsExternal(!isExternal)} styles={{ alignItems: 'center' }}>
                    <input type='checkbox' checked={isExternal} className='pointer' readOnly id="external" />
                    <label htmlFor="external" className={styles["sr-only"]}>{t(`infrastructure.external`)}</label>
                    <p>{t(`storage.external`)}</p>
                </div>
                { isExternal && 
                <div className={(isExternal ? '' : 'hidden ')}>
                        <p className={styles.title}>{t('config')}</p>
                        <div className={styles['config-wrapper']}>
                            <p>{t(`storage.ssd`)}</p>
                            <Dropdown
                                disabled={!isExternal}
                                options={service.storageConfigs.externalValues}
                                onChange={(e) => setStorage(prevState => (
                                    {
                                        ...prevState,
                                        ssd: {
                                            ...prevState.ssd,
                                            size: e.value
                                        }
                                    }))}
                                value={isExternal?.ssd || service?.storageConfigs?.externalValues[0]}
                                className={styles['dropdown-size']}
                            />
                        </div>
                        <div className={styles['config-wrapper']}>
                            <p>{t(`storage.hdd`)}</p>
                            <Dropdown
                                disabled={!isExternal}
                                options={service.storageConfigs.externalValues}
                                onChange={(e) => setStorage(prevState => (
                                    {
                                        ...prevState,
                                        hdd: {
                                            ...prevState.hdd,
                                            size: e.value
                                        }
                                    }))}
                                value={isExternal?.hdd || service?.storageConfigs?.externalValues[0]}
                                className={styles['dropdown-size']}
                            />
                        </div>
                    </div>}
                { !isExternal && <>
                    <div className='flex' style={{ marginTop: '1rem' }}>
                        <h5 style={{ fontSize: '16px' }}>{t('infrastructure.storage')}</h5>
                        <label style={{ marginLeft: 'auto' }}>
                            <p style={{ fontSize: '14px' }}>{t('infrastructure.preset')}</p>&ensp;
                            <Dropdown
                                options={steps.map((step, index) => ({ value: index, label: t(step) }))}
                                onChange={changeStoragePreset}
                                value={storagePreset || 0}
                                className={styles['dropdown-size']}

                            />
                        </label>    
                    </div>
                    <div style={{ display: 'contents' }} className={(isExternal ? 'hidden ' : '')}>
                        <div className={'flex ' + styles.configs} onClick={() => setIsStorageHidden(!isStorageHidden)}>
                            <div className={styles['arrow-container']}>
                                <div className={`${styles['arrow-element']} ${isStorageHidden ? '' : styles['rotate-forward']}`} />
                            </div>
                            <p className={'pointer ' + styles.title}>{t('config')}</p>
                        </div>
                        <div className={styles[isStorageHidden ? 'hide' : 'visible']}>
                            <label>
                                <p className={styles.storage_label}>{t('infrastructure.type')}</p>&ensp;
                                <Dropdown
                                    options={service.storageConfigs.type.values}
                                    onChange={changeStorage('type-hdd-ssd')}
                                    value={storage.ssd.type}
                                    className={`${styles['dropdown-size']} ${styles.type_dropdown}`}
                                />
                            </label>
                            { storage.ssd.type === 2 && <label style={{ marginTop: '0.5rem' }}>
                                <p className={styles.storage_label}>{t(`infrastructure.nodes`)}</p>&ensp;
                                <Dropdown
                                    disabled={isExternal}
                                    options={service.storageConfigs.nodes.values}
                                    onChange={changeStorage('nodes-hdd-ssd')}
                                    value={storage.ssd.nodes || 0}
                                    className={styles['dropdown-size']}
                                />
                            </label> }
                            <div className={styles['storage-options']}>
                                { service.disks.filter(diskType => !(storage.ssd.type === 1 && diskType === 'hdd')).map(disk => (
                                    <div key={disk} className={'flex col ' + styles['storage-type']}>
                                        <h5>{disk.toUpperCase()}</h5>
                                            { storage.ssd.type !== 2 && <div className={styles['config-wrapper']}>
                                                <p>{t(`infrastructure.nodes`)}</p>
                                                <Dropdown
                                                    disabled={isExternal}
                                                    options={service.storageConfigs.nodes.values}
                                                    onChange={changeStorage(`nodes-${disk}`)}
                                                    value={storage[disk].nodes || 0}
                                                    className={styles['dropdown-size']}
                                                />
                                            </div> }
                                            <div className={styles['config-wrapper']}>
                                                <p>{t(`infrastructure.size`)}</p>
                                                <Dropdown
                                                    disabled={isExternal}
                                                    options={service.storageConfigs.size.values}
                                                    onChange={changeStorage(`size-${disk}`)}
                                                    value={storage[disk]?.size || service.size.values[0]}
                                                    className={styles['dropdown-size']}
                                                />
                                            </div>
                                        <label>
                                            <p>{ t(`infrastructure.usable`) }</p>
                                            <p className={styles.edit}>{displayTbOrGb(calculateUsable(disk), t)}</p>
                                        </label>
                                    </div>))
                                }
                            </div>
                        </div>
                    </div>
                </> }
                <br />
                <div className='flex'>
                    <h5 style={{ fontSize: '16px' }}>{t('infrastructure.compute')}</h5>
                    <label style={{ marginLeft: 'auto' }}>
                        <p style={{ fontSize: '14px' }}>{t('infrastructure.preset')}</p>&ensp;
                        <Dropdown
                            options={steps.map((step, index) => ({ value: index, label: t(step) }))}
                            onChange={(e) => {
                                return e.value !== 3 && setComputePreset(Number(e.value))
                            }}
                            value={computePreset || 0}
                            className={styles['dropdown-size']}
                        />
                    </label>
                </div>
                <div className={'flex ' + styles.configs} onClick={() => setIsComputeHidden(!isComputeHidden)}>
                    <div className={styles['arrow-container']}>
                        <div className={`${styles['arrow-element']} ${isComputeHidden ? '' : styles['rotate-forward']}`} />
                    </div>
                    <p className={'pointer ' + styles.title}>{t('config')}</p>
                </div>
                <div className={styles[isComputeHidden ? 'hide' : 'visible']}>
                <div className={'flex col ' + styles['compute-type']}>
                        { service.computeParams.map((option, key) => <div className={styles['config-wrapper']} key={key}>
                            <p>{t(`infrastructure.${option}`)}</p>
                            <Dropdown
                                options={service.computesConfig[option]}
                                onChange={(e) => changeCompute(e, option)}
                                value={compute[option] || service.computesConfig[option][0]}
                                className={styles['dropdown-size']}
                            />
                        </div>)}
                    </div>
                    
                </div>
            </div>
            <style jsx>{`
                select {
                    width: 74px;
                }
            `}</style>
        </>
    )
};

export default memo(InfrastructureConfig);
